import React from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTE_I18N_KEYS, REPORT_I18N_KEYS } from "@/lib/routeLabels";
import {
  Bell,
  ChevronDown,
  HelpCircle,
  Plus,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import BranchCounterSelectors from "./BranchCounterSelectors";
import { REPORT_CENTER_ITEMS } from "@/lib/reportCenterNav";
import { useOnlineOrderInbox } from "@/hooks/useOnlineOrderInbox";
import { isOnlineFeatureEnabled } from "@/lib/permissions";
import { formatStatus } from "@/components/online/OnlineOrderDetailSheet";

const REPORT_ROUTE_LABELS = Object.fromEntries(
  REPORT_CENTER_ITEMS.map((r) => [r.path, r.label]),
);

function getCrumbs(pathname, t) {
  if (pathname === "/") return [{ label: t("nav.dashboard"), to: "/" }];
  const segs = pathname.split("/").filter(Boolean);
  const crumbs = [{ label: t("common.home"), to: "/" }];
  let acc = "";
  segs.forEach((s) => {
    acc += "/" + s;
    const key = ROUTE_I18N_KEYS[acc] || REPORT_I18N_KEYS[acc];
    crumbs.push({
      label: key ? t(key) : REPORT_ROUTE_LABELS[acc] || s.replace(/-/g, " "),
      to: acc,
    });
  });
  return crumbs;
}

function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.floor(diff / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function TopHeader() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const crumbs = getCrumbs(pathname, t);
  const onlineEnabled = isOnlineFeatureEnabled(user);
  const { counts, newOrders, pendingDelivery, badgeTotal } = useOnlineOrderInbox({
    enabled: onlineEnabled && Boolean(user?.permissions?.includes("online.orders.view")),
  });

  const notifications = [
    ...newOrders.map((o) => ({
      id: `new-${o.id}`,
      title: `${t("onlineOrders.newOrders")}: ${o.reference} · ${o.customer_name}`,
      time: timeAgo(o.placed_at),
      type: "info",
      to: `/online/orders?order=${o.id}&attention=1`,
    })),
    ...pendingDelivery.map((o) => ({
      id: `del-${o.id}`,
      title: `${t("onlineOrders.pendingDelivery")}: ${o.reference} · ${formatStatus(o.status)}`,
      time: timeAgo(o.placed_at),
      type: "warn",
      to: `/online/orders?order=${o.id}&attention=1`,
    })),
  ].slice(0, 12);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <header
      data-testid="app-header"
      className="h-12 flex items-center px-2 sm:px-3 bg-[#FFFCF9] border-b border-[#E8D5C8] gap-2 sm:gap-2.5 sticky top-0 z-30 min-w-0 overflow-hidden shadow-[0_2px_8px_rgba(43,11,4,0.06)]"
    >
      <nav
        data-testid="breadcrumbs"
        className="hidden sm:flex items-center text-[12px] text-brand-brown/45 min-w-0 max-w-[28%] lg:max-w-[22%] overflow-hidden"
      >
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <React.Fragment key={c.to}>
              {i > 0 && <span className="mx-1.5 text-brand-cream-border">/</span>}
              {isLast ? (
                <span className="text-brand-brown font-semibold truncate">{c.label}</span>
              ) : (
                <Link className="hover:text-brand-orange truncate transition-colors" to={c.to}>
                  {c.label}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0">
        <BranchCounterSelectors />

        <button
          data-testid="help-btn"
          className="hidden sm:grid h-8 w-8 place-items-center rounded-lg text-brand-brown/45 hover:bg-brand-cream-panel hover:text-brand-brown transition-colors"
          title={t("common.help")}
        >
          <HelpCircle size={15} />
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="notifications-btn"
              className="relative h-8 w-8 grid place-items-center rounded-lg text-brand-brown/45 hover:bg-brand-cream-panel hover:text-brand-brown transition-colors"
              title={t("header.notifications")}
            >
              <Bell size={16} />
              {badgeTotal > 0 ? (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-[9px] font-bold text-white flex items-center justify-center ring-2 ring-white">
                  {badgeTotal > 99 ? "99+" : badgeTotal}
                </span>
              ) : null}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0 border-brand-cream-border">
            <div className="px-3 py-2.5 border-b border-brand-cream-border text-[13px] font-semibold flex items-center justify-between text-brand-brown">
              {t("header.notifications")}{" "}
              <Badge variant="secondary" className="text-[10px]">
                {badgeTotal}
              </Badge>
            </div>
            {(counts.new > 0 || counts.pending_delivery > 0) && (
              <div className="flex gap-2 px-3 py-2 border-b border-brand-cream-border bg-orange-50/50 text-[11px]">
                <span className="rounded-full bg-orange-100 px-2 py-0.5 font-semibold text-orange-900">
                  {t("onlineOrders.newOrders")}: {counts.new || 0}
                </span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
                  {t("onlineOrders.pendingDelivery")}: {counts.pending_delivery || 0}
                </span>
              </div>
            )}
            <ul className="divide-y divide-brand-cream-border max-h-72 overflow-y-auto">
              {notifications.length === 0 && (
                <li className="px-3 py-6 text-center text-[12px] text-brand-brown/40">
                  No pending online or delivery alerts
                </li>
              )}
              {notifications.map((n) => (
                <li key={n.id}>
                  <Link to={n.to} className="block px-3 py-2.5 hover:bg-brand-cream/60">
                    <div className="flex items-start gap-2.5">
                      <span
                        className="h-1.5 w-1.5 rounded-full mt-1.5"
                        style={{
                          background: n.type === "warn" ? "#F59E0B" : "#F36F21",
                        }}
                      />
                      <div className="text-[12.5px]">
                        <div className="text-brand-brown">{n.title}</div>
                        <div className="text-brand-brown/40 text-[11px] mt-0.5">{n.time}</div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/online/orders?attention=1"
              className="block border-t border-brand-cream-border p-2 text-center text-[12px] text-brand-orange hover:bg-brand-cream/60 font-medium"
            >
              {t("header.viewAllNotifications")}
            </Link>
          </PopoverContent>
        </Popover>

        <Link
          to="/pos"
          className="hidden sm:inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-brand-orange hover:bg-[#E05E10] text-white text-[12px] font-bold shadow-[0_2px_8px_rgba(243,111,33,0.35)] transition-colors"
          data-testid="header-new-order"
        >
          <Plus size={14} />
          <span className="hidden md:inline">{t("dashboard.newOrder")}</span>
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-btn"
              className="flex items-center gap-2 h-8 px-1.5 sm:px-2 rounded-lg hover:bg-brand-cream-panel transition-colors"
            >
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-brand-orange to-[#D95A0E] grid place-items-center text-white text-[11px] font-bold shadow-sm">
                {initials(user?.name)}
              </div>
              <div className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-[12.5px] font-semibold text-brand-brown">{user?.name}</span>
                <span className="text-[10.5px] text-brand-brown/45 capitalize">
                  {user?.role || "user"} • {user?.default_counter?.name || "—"}
                </span>
              </div>
              <ChevronDown size={14} className="text-brand-brown/35 hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 border-brand-cream-border">
            <DropdownMenuLabel>{t("header.myAccount")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>{t("header.profile")}</DropdownMenuItem>
            <DropdownMenuItem>{t("header.changePassword")}</DropdownMenuItem>
            <DropdownMenuItem>{t("header.counterActivity")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
              {t("header.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
